import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Gift,
  Tag,
  Sparkles,
  Users,
  Heart,
  Calendar,
  ArrowRight,
  Mail,
  Repeat,
  Repeat2,
  Home,
} from "lucide-react";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { value: "weekly",   label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly",  label: "Monthly" },
];

const SubscriptionPricing = () => {
  const [catalog, setCatalog] = useState(null);
  const [activeTier, setActiveTier] = useState("standard");

  useEffect(() => {
    axios.get(`${API}/subscriptions/catalog`)
      .then((r) => setCatalog(r.data))
      .catch((e) => logError("catalog fetch failed", e));
  }, []);

  if (!catalog) return null;

  const tier = catalog.tiers[activeTier];
  const bedrooms = [1, 2, 3, 4, 5, 6, 7, 8];

  return (
    <section id="membership" className="py-20 bg-white" data-testid="subscription-pricing">
      <div className="container-clean">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Repeat2 className="w-4 h-4" />
            Subscriptions
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#1A2E12] tracking-tight mb-3">
            Luxury cleaning, on a schedule.
          </h2>
          <p className="text-[#5C6B57] max-w-2xl mx-auto">
            Standard and Deep Clean — Weekly, Bi-Weekly or Monthly. London &amp; Birmingham. 1–8 bedroom properties.
            Cancel any time with 30 days&apos; notice (see <Link to="/legal#subscription" className="underline">Subscription Policy</Link>).
          </p>
        </div>

        {/* Tier switch */}
        <div className="flex justify-center gap-2 mb-8">
          {Object.entries(catalog.tiers).map(([slug, t]) => (
            <button
              key={slug}
              type="button"
              onClick={() => setActiveTier(slug)}
              className={`px-5 py-2 rounded-full font-medium text-sm transition-all ${
                activeTier === slug
                  ? "bg-[#2D5016] text-white"
                  : "bg-white border border-[#E2E8E0] text-[#5C6B57] hover:border-[#2D5016]"
              }`}
              data-testid={`pricing-tier-${slug}`}
            >
              {t.name}
            </button>
          ))}
        </div>

        <p className="text-center text-sm text-[#5C6B57] mb-6 max-w-2xl mx-auto">
          {tier.description}
        </p>

        {/* Pricing table */}
        <div className="overflow-x-auto bg-[#F9FAF9] rounded-2xl border border-[#E2E8E0] p-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="p-3 text-[#5C6B57] font-semibold">Bedrooms</th>
                {FREQUENCIES.map((f) => (
                  <th key={f.value} className="p-3 text-[#5C6B57] font-semibold">{f.label}</th>
                ))}
                <th className="p-3" />
              </tr>
            </thead>
            <tbody className="bg-white">
              {bedrooms.map((b) => (
                <tr key={b} className="border-t border-[#E2E8E0]" data-testid={`pricing-row-${activeTier}-${b}bed`}>
                  <td className="p-3 font-medium text-[#1A2E12]">{b} Bed</td>
                  {FREQUENCIES.map((f) => (
                    <td key={f.value} className="p-3 text-[#1A2E12]">
                      £{tier.prices[b][f.value]}
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <Link to={`/booking?tier=${activeTier}&frequency=monthly&bedrooms=${b}`}>
                      <Button size="sm" variant="outline" className="border-[#2D5016] text-[#2D5016] rounded-full" data-testid={`pricing-book-${activeTier}-${b}bed`}>
                        Book
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add-ons */}
        <div className="mt-10">
          <h3 className="text-lg font-semibold text-[#1A2E12] mb-4">Optional add-ons</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(catalog.addons).map(([slug, a]) => (
              <div
                key={slug}
                className="bg-white border border-[#E2E8E0] rounded-xl p-4 flex items-start justify-between gap-3"
                data-testid={`addon-card-${slug}`}
              >
                <div>
                  <p className="font-semibold text-[#1A2E12]">{a.name}</p>
                  <p className="text-xs text-[#5C6B57] mt-1">{a.description}</p>
                </div>
                <span className="text-sm font-bold text-[#2D5016] whitespace-nowrap">
                  {a.price_pct ? `+${a.price_pct}%` : `£${a.price}`}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-[#5C6B57] mt-8">
          End-of-Tenancy cleans are available as one-off bookings only — not on subscription.
        </p>
      </div>
    </section>
  );
};

// Static, brand-curated offers — easy to edit, no CMS needed.
const NEW_CUSTOMER_OFFERS = [
  {
    id: "first-clean",
    icon: Sparkles,
    badge: "New customers",
    title: "20% off your first clean",
    description:
      "Try us out with confidence. Get 20% off your first General, Deep, or End-of-Tenancy clean — applied automatically when you book.",
    cta: "Book your first clean",
    code: "WELCOME20",
  },
  {
    id: "refer-friend",
    icon: Users,
    badge: "Refer a friend",
    title: "£25 for you, £25 for them",
    description:
      "Love your clean? Refer a friend and you both get £25 off your next visit. No limit — refer as many friends as you like.",
    cta: "Refer a friend",
    code: "REFER25",
  },
];

const EXISTING_CUSTOMER_OFFERS = [
  {
    id: "recurring",
    icon: Repeat,
    badge: "Existing customers",
    title: "Up to 15% off recurring cleans",
    description:
      "Switch to a weekly, fortnightly or monthly schedule and save up to 15% on every clean. Same trusted cleaner, locked-in pricing.",
    cta: "Set up recurring",
    code: "RECURRING15",
  },
  {
    id: "seasonal",
    icon: Gift,
    badge: "Seasonal",
    title: "Spring deep clean — £30 off",
    description:
      "Refresh your home for the new season. £30 off any deep clean booked through our spring window. Limited dates available.",
    cta: "Book a deep clean",
    code: "SPRING30",
  },
  {
    id: "loyalty",
    icon: Heart,
    badge: "Loyalty",
    title: "5th clean on us",
    description:
      "Book five general cleans on a recurring subscription and your sixth visit is free. Loyalty pays — literally.",
    cta: "Start your streak",
    code: "LOYALTY6",
  },
  {
    id: "tenancy",
    icon: Home,
    badge: "Landlords & agents",
    title: "10% off End of Tenancy packs",
    description:
      "Manage multiple properties? Get 10% off any 5+ End of Tenancy bookings in a rolling month. Email us for partnership rates.",
    cta: "Email us",
    code: "EOT10",
  },
];

const OFFER_TERMS = [
  "One offer per booking unless explicitly stated.",
  "Offers apply to residential services only.",
  "Discount codes must be added at checkout or referenced when emailing.",
  "Offers are subject to availability and may be withdrawn without notice.",
  "Existing recurring bookings retain their original discount.",
];

const OfferCard = ({ offer, index }) => (
  <motion.div
    key={offer.id}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
  >
    <Card
      className="h-full bg-white border-[#E2E8E0] rounded-2xl hover:shadow-xl transition-all"
      data-testid={`offer-card-${offer.id}`}
    >
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
            <offer.icon className="w-6 h-6 text-[#2D5016]" strokeWidth={1.5} />
          </div>
          <span className="text-xs text-[#7CB342] font-semibold uppercase tracking-widest bg-[#F1F8E9] px-3 py-1 rounded-full">
            {offer.badge}
          </span>
        </div>
        <h3 className="text-xl font-semibold text-[#1A2E12] mb-2">
          {offer.title}
        </h3>
        <p className="text-[#5C6B57] leading-relaxed flex-1">
          {offer.description}
        </p>
        <div className="mt-4 flex items-center gap-2 bg-[#F9FAF9] border border-dashed border-[#7CB342] rounded-lg px-3 py-2">
          <Tag className="w-4 h-4 text-[#7CB342]" />
          <span className="text-xs text-[#5C6B57]">Use code</span>
          <code className="font-mono font-semibold text-[#2D5016]">
            {offer.code}
          </code>
        </div>
        <div className="mt-4 flex gap-2">
          <Link to={`/booking?code=${offer.code}`} className="flex-1">
            <Button
              className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
              data-testid={`offer-book-${offer.id}`}
            >
              {offer.cta}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const OffersPage = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Gift className="w-4 h-4" />
              Offers
            </span>
            <h1
              className="text-5xl md:text-6xl font-bold text-[#1A2E12] tracking-tight mb-6"
              data-testid="offers-title"
            >
              Special offers,<br />
              <span className="text-[#2D5016]">
                made to be used.
              </span>
            </h1>
            <p className="text-lg text-[#5C6B57] leading-relaxed">
              We&apos;d rather pass savings on to you than spend them on
              advertising. Here&apos;s what&apos;s currently running for new
              and returning customers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* New customer */}
      <section className="py-16" data-testid="new-customer-offers">
        <div className="container-clean">
          <div className="flex items-center gap-3 mb-8">
            <Sparkles className="w-6 h-6 text-[#7CB342]" />
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1A2E12]">
              For new customers
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {NEW_CUSTOMER_OFFERS.map((o, i) => (
              <OfferCard offer={o} index={i} key={o.id} />
            ))}
          </div>
        </div>
      </section>

      {/* SUBSCRIPTION PRICING MATRIX */}
      <SubscriptionPricing />

      {/* Existing customer */}
      <section className="py-16 bg-[#F9FAF9]" data-testid="existing-customer-offers">
        <div className="container-clean">
          <div className="flex items-center gap-3 mb-8">
            <Heart className="w-6 h-6 text-[#7CB342]" />
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1A2E12]">
              For existing customers
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXISTING_CUSTOMER_OFFERS.map((o, i) => (
              <OfferCard offer={o} index={i} key={o.id} />
            ))}
          </div>
        </div>
      </section>

      {/* Custom offer / Email us */}
      <section className="py-16" data-testid="custom-offer">
        <div className="container-clean">
          <div className="bg-[#2D5016] rounded-3xl p-10 md:p-14 text-center text-white max-w-4xl mx-auto">
            <Calendar className="w-10 h-10 text-[#7CB342] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">
              Looking for something custom?
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Multi-property, multi-site, or a corporate gift package — drop
              us a line and we&apos;ll put together a bespoke offer for your
              specific needs.
            </p>
            <EnquiryDialog source="offers-custom" subject="Custom offer enquiry">
              <Button
                className="bg-white text-[#2D5016] hover:bg-[#E8F5E9] rounded-full px-8 py-6 text-lg"
                data-testid="offers-email-cta"
              >
                <Mail className="mr-2 w-5 h-5" />
                Email us
              </Button>
            </EnquiryDialog>
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-12 bg-[#F9FAF9]">
        <div className="container-clean max-w-3xl">
          <h3 className="text-sm font-semibold text-[#5C6B57] uppercase tracking-widest mb-4">
            Offer terms
          </h3>
          <ul className="space-y-2 text-sm text-[#5C6B57]">
            {OFFER_TERMS.map((t) => (
              <li key={t} className="flex items-start gap-2">
                <span className="text-[#7CB342] mt-1">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </Layout>
  );
};

export default OffersPage;
